import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, User as UserIcon, Buildings, CheckCircle } from '@phosphor-icons/react';
import { api, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ChoiceCard, Chip, Field, inputClass } from '../components/onboarding/Choices';
import {
  COUNTRIES,
  LANGUAGES,
  PROFESSIONAL_BACKGROUNDS,
  ORGANIZATION_TYPES,
  PROPERTY_INTERESTS,
  TICKET_RANGES,
  OBJECTIVES,
  EXPERIENCE_LEVELS,
  HEARD_ABOUT,
} from '../lib/investorProfileOptions';

const YEARS = Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - 18 - i);

const OnboardingPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [investorType, setInvestorType] = useState(null);
  const [profile, setProfile] = useState({ preferred_language: 'en' });
  const [prefs, setPrefs] = useState({ property_interests: [], marketing_consent: false });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);

  const isCompany = investorType === 'company';
  const setField = (patch) => setProfile((prev) => ({ ...prev, ...patch }));
  const setPref = (patch) => setPrefs((prev) => ({ ...prev, ...patch }));

  // Pick up anything already on file, so an existing investor is never asked twice.
  useEffect(() => {
    api
      .get('/onboarding/profile/state')
      .then(({ data }) => {
        // Already done, however they got here - never show the flow a second time.
        if (data.onboarding_completed) {
          navigate('/app/home', { replace: true });
          return;
        }
        setInvestorType(data.investor_type || null);
        setProfile((prev) => ({ ...prev, ...(data.profile || {}) }));
        setPrefs((prev) => ({ ...prev, ...(data.preferences || {}) }));
        setLoading(false);
      })
      .catch(() => setLoading(false));
    api.post('/onboarding/profile/events', { event: 'onboarding_started' }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canContinueStep2 = useMemo(() => {
    if (isCompany) {
      return Boolean(
        profile.organization_name &&
          profile.organization_type &&
          profile.country_of_incorporation &&
          profile.representative_first_name &&
          profile.representative_last_name &&
          profile.preferred_language
      );
    }
    return Boolean(
      profile.first_name && profile.last_name && profile.country_of_residence && profile.preferred_language
    );
  }, [isCompany, profile]);

  const goToStep = (next, event) => {
    setStep(next);
    if (event) api.post('/onboarding/profile/events', { event }).catch(() => {});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.post('/onboarding/profile/complete', {
        investor_type: investorType || 'individual',
        profile,
        preferences: prefs,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'We could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (value) =>
    setPref({
      property_interests: prefs.property_interests.includes(value)
        ? prefs.property_interests.filter((v) => v !== value)
        : [...prefs.property_interests, value],
    });

  // Nobody should see the onboarding shell before we know whether they even need it.
  if (loading) {
    return <div className="min-h-[60vh]" data-testid="onboarding-loading" />;
  }

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="onboarding-complete">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-lg"
        >
          <CheckCircle size={56} weight="fill" className="text-primary mx-auto mb-7" />
          <h1 className="text-4xl md:text-5xl font-heading font-medium tracking-tight">You're all set.</h1>
          <p className="text-lg text-foreground-muted mt-4">Welcome to a better way into real estate.</p>
          <Button
            onClick={() => navigate('/projects')}
            className="mt-9 h-12 px-7 rounded-full bg-primary hover:bg-primary-hover text-white text-base"
            data-testid="explore-investments-button"
          >
            Explore Investments <ArrowRight size={18} className="ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="px-6 py-10 md:py-16" data-testid="onboarding-page">
      <div className="mx-auto w-full max-w-[820px]">
        <div className="flex items-center gap-2 mb-10" data-testid="onboarding-progress">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                index <= step ? 'bg-primary' : 'bg-black/[0.08]'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {step === 1 && (
              <div data-testid="onboarding-step-1">
                <h1 className="text-4xl md:text-5xl font-heading font-medium tracking-tight leading-[1.08]">
                  Welcome to Flippex.
                </h1>
                <p className="text-lg text-foreground-muted mt-4 max-w-xl">
                  Your journey into real estate investing starts here. Let's get to know you a
                  little better.
                </p>
                {user?.email && (
                  <p className="text-sm text-foreground-muted mt-6" data-testid="onboarding-email">
                    Signed in as <span className="text-foreground font-medium">{user.email}</span>
                  </p>
                )}

                <h2 className="text-base md:text-lg font-medium mt-12 mb-5">How will you be investing?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ChoiceCard
                    selected={investorType === 'individual'}
                    onClick={() => setInvestorType('individual')}
                    title="Individual"
                    subtitle="I'm investing personally."
                    icon={UserIcon}
                    testId="investor-type-individual"
                  />
                  <ChoiceCard
                    selected={isCompany}
                    onClick={() => setInvestorType('company')}
                    title="Company / Institution"
                    subtitle="I'm investing on behalf of an organization."
                    icon={Buildings}
                    testId="investor-type-company"
                  />
                </div>

                <Button
                  onClick={() => goToStep(2, 'investor_type_selected')}
                  disabled={!investorType}
                  className="mt-10 h-12 px-7 rounded-full bg-primary hover:bg-primary-hover text-white text-base"
                  data-testid="onboarding-continue-1"
                >
                  Continue <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="onboarding-step-2">
                <h1 className="text-3xl md:text-4xl font-heading font-medium tracking-tight">
                  {isCompany ? 'About your organization.' : 'A little about you.'}
                </h1>
                <p className="text-foreground-muted mt-3">
                  Just the basics — we'll only ask for anything more when you invest.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-9">
                  {isCompany ? (
                    <>
                      <div className="sm:col-span-2">
                        <Field label="Organization name" htmlFor="organization_name">
                          <input
                            id="organization_name"
                            className={inputClass}
                            value={profile.organization_name || ''}
                            onChange={(e) => setField({ organization_name: e.target.value })}
                            data-testid="input-organization-name"
                          />
                        </Field>
                      </div>
                      <Field label="Organization type" htmlFor="organization_type">
                        <select
                          id="organization_type"
                          className={inputClass}
                          value={profile.organization_type || ''}
                          onChange={(e) => setField({ organization_type: e.target.value })}
                          data-testid="select-organization-type"
                        >
                          <option value="">Select…</option>
                          {ORGANIZATION_TYPES.map((type) => (
                            <option key={type}>{type}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Country of incorporation" htmlFor="country_of_incorporation">
                        <input
                          id="country_of_incorporation"
                          list="country-list"
                          className={inputClass}
                          value={profile.country_of_incorporation || ''}
                          onChange={(e) => setField({ country_of_incorporation: e.target.value })}
                          data-testid="input-country-incorporation"
                        />
                      </Field>
                      <Field label="Representative first name" htmlFor="rep_first">
                        <input
                          id="rep_first"
                          className={inputClass}
                          value={profile.representative_first_name || ''}
                          onChange={(e) => setField({ representative_first_name: e.target.value })}
                          data-testid="input-rep-first-name"
                        />
                      </Field>
                      <Field label="Representative last name" htmlFor="rep_last">
                        <input
                          id="rep_last"
                          className={inputClass}
                          value={profile.representative_last_name || ''}
                          onChange={(e) => setField({ representative_last_name: e.target.value })}
                          data-testid="input-rep-last-name"
                        />
                      </Field>
                      <Field label="Representative role" optional htmlFor="rep_role">
                        <input
                          id="rep_role"
                          className={inputClass}
                          value={profile.representative_role || ''}
                          onChange={(e) => setField({ representative_role: e.target.value })}
                          data-testid="input-rep-role"
                        />
                      </Field>
                      <Field label="Company website" optional htmlFor="company_website">
                        <input
                          id="company_website"
                          className={inputClass}
                          placeholder="https://"
                          value={profile.company_website || ''}
                          onChange={(e) => setField({ company_website: e.target.value })}
                          data-testid="input-company-website"
                        />
                      </Field>
                    </>
                  ) : (
                    <>
                      <Field label="First name" htmlFor="first_name">
                        <input
                          id="first_name"
                          className={inputClass}
                          value={profile.first_name || ''}
                          onChange={(e) => setField({ first_name: e.target.value })}
                          data-testid="input-first-name"
                        />
                      </Field>
                      <Field label="Last name" htmlFor="last_name">
                        <input
                          id="last_name"
                          className={inputClass}
                          value={profile.last_name || ''}
                          onChange={(e) => setField({ last_name: e.target.value })}
                          data-testid="input-last-name"
                        />
                      </Field>
                      <Field label="Country of residence" htmlFor="country_of_residence">
                        <input
                          id="country_of_residence"
                          list="country-list"
                          className={inputClass}
                          placeholder="Start typing…"
                          value={profile.country_of_residence || ''}
                          onChange={(e) => setField({ country_of_residence: e.target.value })}
                          data-testid="input-country-residence"
                        />
                      </Field>
                      <Field label="Nationality" optional htmlFor="nationality">
                        <input
                          id="nationality"
                          list="country-list"
                          className={inputClass}
                          value={profile.nationality || ''}
                          onChange={(e) => setField({ nationality: e.target.value })}
                          data-testid="input-nationality"
                        />
                      </Field>
                      <Field label="Phone number" optional htmlFor="phone">
                        <input
                          id="phone"
                          type="tel"
                          className={inputClass}
                          placeholder="+351 …"
                          value={profile.phone || ''}
                          onChange={(e) => setField({ phone: e.target.value })}
                          data-testid="input-phone"
                        />
                      </Field>
                      <Field label="Year of birth" optional htmlFor="year_of_birth">
                        <select
                          id="year_of_birth"
                          className={inputClass}
                          value={profile.year_of_birth || ''}
                          onChange={(e) =>
                            setField({ year_of_birth: e.target.value ? Number(e.target.value) : null })
                          }
                          data-testid="select-year-of-birth"
                        >
                          <option value="">Select…</option>
                          {YEARS.map((year) => (
                            <option key={year}>{year}</option>
                          ))}
                        </select>
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Professional background" optional htmlFor="professional_background">
                          <select
                            id="professional_background"
                            className={inputClass}
                            value={profile.professional_background || ''}
                            onChange={(e) => setField({ professional_background: e.target.value })}
                            data-testid="select-professional-background"
                          >
                            <option value="">Select…</option>
                            {PROFESSIONAL_BACKGROUNDS.map((item) => (
                              <option key={item}>{item}</option>
                            ))}
                          </select>
                        </Field>
                      </div>
                    </>
                  )}

                  <Field label="Preferred language" htmlFor="preferred_language">
                    <select
                      id="preferred_language"
                      className={inputClass}
                      value={profile.preferred_language || 'en'}
                      onChange={(e) => setField({ preferred_language: e.target.value })}
                      data-testid="select-language"
                    >
                      {LANGUAGES.map((language) => (
                        <option key={language.value} value={language.value}>
                          {language.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <datalist id="country-list">
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country} />
                  ))}
                </datalist>

                <div className="flex items-center gap-3 mt-10">
                  <Button
                    variant="ghost"
                    onClick={() => goToStep(1)}
                    className="h-12 px-5 rounded-full"
                    data-testid="onboarding-back-2"
                  >
                    <ArrowLeft size={18} className="mr-2" /> Back
                  </Button>
                  <Button
                    onClick={() => goToStep(3, 'basic_profile_completed')}
                    disabled={!canContinueStep2}
                    className="h-12 px-7 rounded-full bg-primary hover:bg-primary-hover text-white text-base"
                    data-testid="onboarding-continue-2"
                  >
                    Continue <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="onboarding-step-3">
                <h1 className="text-3xl md:text-4xl font-heading font-medium tracking-tight">
                  Make Flippex yours.
                </h1>
                <p className="text-foreground-muted mt-3">
                  Tell us what interests you so we can make your investment experience more
                  relevant.
                </p>

                <section className="mt-10">
                  <h2 className="text-base md:text-lg font-medium mb-4">
                    What types of real estate interest you?
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PROPERTY_INTERESTS.map((interest) => (
                      <ChoiceCard
                        key={interest.value}
                        selected={prefs.property_interests.includes(interest.value)}
                        onClick={() => toggleInterest(interest.value)}
                        title={interest.label}
                        subtitle={interest.hint}
                        testId={`interest-${interest.value}`}
                      />
                    ))}
                  </div>
                </section>

                <section className="mt-10">
                  <h2 className="text-base md:text-lg font-medium mb-4">
                    How much would you typically consider investing per project?
                  </h2>
                  <div className="flex flex-wrap gap-2.5">
                    {TICKET_RANGES.map((range) => (
                      <Chip
                        key={range}
                        selected={prefs.ticket_range === range}
                        onClick={() => setPref({ ticket_range: range })}
                        testId={`ticket-${range}`}
                      >
                        {range}
                      </Chip>
                    ))}
                  </div>
                  <p className="text-xs text-foreground-muted mt-3">
                    An indication of interest — not a commitment.
                  </p>
                </section>

                <section className="mt-10">
                  <h2 className="text-base md:text-lg font-medium mb-4">
                    What is your primary investment objective?
                  </h2>
                  <div className="flex flex-wrap gap-2.5">
                    {OBJECTIVES.map((objective) => (
                      <Chip
                        key={objective}
                        selected={prefs.objective === objective}
                        onClick={() => setPref({ objective })}
                        testId={`objective-${objective}`}
                      >
                        {objective}
                      </Chip>
                    ))}
                  </div>
                </section>

                <section className="mt-10">
                  <h2 className="text-base md:text-lg font-medium mb-4">
                    How experienced are you with real estate investing?
                  </h2>
                  <div className="flex flex-wrap gap-2.5">
                    {EXPERIENCE_LEVELS.map((level) => (
                      <Chip
                        key={level}
                        selected={prefs.experience === level}
                        onClick={() => setPref({ experience: level })}
                        testId={`experience-${level}`}
                      >
                        {level}
                      </Chip>
                    ))}
                  </div>
                </section>

                <section className="mt-10">
                  <h2 className="text-base md:text-lg font-medium mb-4">How did you hear about Flippex?</h2>
                  <div className="flex flex-wrap gap-2.5">
                    {HEARD_ABOUT.map((channel) => (
                      <Chip
                        key={channel}
                        selected={prefs.heard_about === channel}
                        onClick={() => setPref({ heard_about: channel })}
                        testId={`heard-${channel}`}
                      >
                        {channel}
                      </Chip>
                    ))}
                  </div>
                  {prefs.heard_about === 'Friend / Referral' && (
                    <input
                      className={`${inputClass} mt-4 max-w-sm`}
                      placeholder="Referral code or name (optional)"
                      value={prefs.referral_detail || ''}
                      onChange={(e) => setPref({ referral_detail: e.target.value })}
                      data-testid="input-referral"
                    />
                  )}
                </section>

                <label className="flex items-start gap-3 mt-10 cursor-pointer max-w-xl">
                  <input
                    type="checkbox"
                    checked={prefs.marketing_consent}
                    onChange={(e) => setPref({ marketing_consent: e.target.checked })}
                    className="mt-1 w-4 h-4 rounded border-black/20 text-primary focus:ring-primary"
                    data-testid="marketing-consent"
                  />
                  <span className="text-sm text-foreground-muted">
                    Email me new projects and updates. Separate from your account — you can change
                    it any time in settings.
                  </span>
                </label>

                <p className="text-xs text-foreground-muted mt-6 max-w-xl">
                  These answers personalise what you see. They are not an assessment of whether an
                  investment is suitable for you.
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-8">
                  <Button
                    variant="ghost"
                    onClick={() => goToStep(2)}
                    className="h-12 px-5 rounded-full"
                    data-testid="onboarding-back-3"
                  >
                    <ArrowLeft size={18} className="mr-2" /> Back
                  </Button>
                  <Button
                    onClick={finish}
                    disabled={saving}
                    className="h-12 px-7 rounded-full bg-primary hover:bg-primary-hover text-white text-base"
                    data-testid="onboarding-finish"
                  >
                    {saving ? 'Saving…' : 'Finish'} <ArrowRight size={18} className="ml-2" />
                  </Button>
                  <button
                    type="button"
                    onClick={finish}
                    disabled={saving}
                    className="text-sm text-foreground-muted hover:text-foreground transition-colors"
                    data-testid="onboarding-skip"
                  >
                    Skip for now
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
